import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { SubjectType } from 'app/shared/models/subject-type';
import { Subscription } from 'rxjs';
import { SubjectTypeService } from 'app/shared/services/subject-type.service';
import { CountWidgetSettings } from 'app/shared/models/count-widget-settings';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { LabelService } from '../../label.service';
import { UserService } from 'app/shared/services/user.service';
import { Utilities } from 'app/shared/helpers/utilities';
import { OrganisationService } from 'app/shared/services/organisation.service';
import { Organisation } from 'app/shared/models/organisations';
import { environment } from 'environments/environment';

@Component({
  selector: 'referral-count-widget-settings',
  templateUrl: './referral-count-widget-settings.component.html',
  styleUrls: ['./referral-count-widget-settings.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ReferralCountWidgetSettingsComponent implements OnInit, OnDestroy {

  form: UntypedFormGroup;

  onTypesChanged: Subscription;
  activeUsers: Subscription;
  onExtraControlValueChanges: Subscription;

  contexts = this.labelService.CONTEXTS
  contextGeneral = this.labelService.CONTEXTS
  contextUsers: any[] = [];
  types: SubjectType[] = [];

  settings: CountWidgetSettings;
  displayExtra = false;
  color: string;
  originalColor: string;

  labelChanged = false;
  titleChanged = false;
  extraLabelChanged = false;
  prevContextValue: any;
  dlgTitle = 'Referral Count Widget Settings';
  currentSubjectTypeId = -1;
  currentExtraSubjectTypeId = -1;

  labelMaxLength = this.labelService.MAXLENGTH;
  titleMaxLength = this.labelService.MAXLENGTH;

  organisations: Organisation[] = [];
  organisationLabel = 'Organisation';
  hasOrganisation = false;
  showOrganisations = false;
  onOrganisationsChanged: Subscription;

  constructor(
    public dialogRef: MatDialogRef<ReferralCountWidgetSettingsComponent>,
    private subjectTypeService: SubjectTypeService,
    private labelService: LabelService,
    private userService: UserService,
    private organisationService: OrganisationService,
    @Inject(MAT_DIALOG_DATA) public data: CountWidgetSettings,
    private fb: UntypedFormBuilder) {
        
      this.settings = data || new CountWidgetSettings();
      if (this.userService.getCurrentUser().organisationId && this.settings.organisationId==undefined)
        this.settings.organisationId = this.userService.getCurrentUser().organisationId;

    this.settings.color = this.settings.color || '#f44336'; // 'rgb(244, 67, 54)';

    this.currentSubjectTypeId = this.settings.typeId;
    this.currentExtraSubjectTypeId = this.settings.extraTypeId;
  }

  ngOnInit() {
    this.onTypesChanged = this.subjectTypeService.onChanged.subscribe((response) => {
      if (response.length > 0) {
        const types = response.filter((type) => ((type.hideRecord !== true) ||
          (this.settings && ((type.id === this.settings.typeId) || (type.id === this.settings.extraTypeId)))));

        // MRD - see #9513.  Logically we shouldn't assign status to non primary subject,
        // but it appears we currently can, so don't filter here so we are at least consistent
        // if (this.settings.showFlags === 2) {
        //   types = types.filter((i) => i.primary);
        // }

        types.sort((a, b) => a.name.localeCompare(b.name));
        this.types = types;
      }
    });

    this.activeUsers = this.userService.getActiveUsers().subscribe((users) => {
      const meId = this.userService.getCurrentUserId();
      this.contextUsers = [];
      users.forEach((i) => {
        if ((i.id === meId) || (i.managerId === meId)) {
          this.contextUsers.push({ id: i.id, name: (i.id === meId) ? 'Me' : i.fullName });
        }
      });
      this.contextUsers.sort((a, b) => a.name.localeCompare(b.name)).forEach((i) => {
        this.contexts.push(i);
      });
    });

    this.onOrganisationsChanged = this.organisationService.onChanged.subscribe((response) => {
      if (response.length > 0) {
        var organisations = response.filter(
          (organisation) =>
            organisation.hideRecord !== true ||
            (this.settings &&
              this.settings.organisationId &&
              organisation.id === this.settings.organisationId)
        );

        if (this.userService.hasPriv('multiorgadmin')) {
          var userOrgs = this.userService.getCurrentUser()?.userOrganisations;
          if (environment.limitAccessToSubjectsByOrg && userOrgs && userOrgs.length > 0 && this.userService.getCurrentUser()) {
            organisations = organisations.filter(i => userOrgs.findIndex(j => j.organisationId == i.id) > -1);
          }
        }
        else if (environment.limitAccessToSubjectsByOrg && this.userService.getCurrentUser().role.toLocaleLowerCase() !='admin') {
          organisations = organisations.filter(i => i.id == this.userService.getCurrentUser()?.organisationId);
        }

        if (organisations.length > 1) {
          organisations.sort((a, b) => a.name.localeCompare(b.name));
          organisations = organisations.filter(i=>{ return (i.id!=-1)});
          organisations.unshift({ id: -1, name: 'All' });
        }

        this.organisations = organisations;
      }
    });

    this.showOrganisations = this.organisationService.showOrganisations;
    if (this.showOrganisations) {
      this.organisationService.getAll();
      if (this.settings.typeId) {
        const subjectType = this.types.find((i) => i.id === this.settings.typeId);
        if (subjectType) {
          this.hasOrganisation = this.organisationService.hasOrganisation(subjectType);
          this.organisationLabel = this.organisationService.organisationLabel(subjectType.name);
        }
      }
      else {
        this.organisationLabel = this.organisationService.organisationLabel();
      }
    }


    this.subjectTypeService.getAll();

    // check title and labels
    if (this.settings.label) {
      const label = this.labelService.getDefaultCountLabel(this.settings).toUpperCase();
      this.labelChanged = !!(label && label !== this.settings.label);
    }

    if (this.settings.extraLabel) {
      const extraLabel = this.labelService.getDefaultExtraCountLabel(this.settings);
      this.extraLabelChanged = !!(extraLabel && (extraLabel !== this.settings.extraLabel));
    }

    if (this.settings.title) {
      const title = this.labelService.getDefaultTitle(this.settings, undefined);
      this.titleChanged = !!(title && (title !== this.settings.title));
    }

    this.prevContextValue = this.settings.context;
    if (this.settings.context) {
      this.prevContextValue = this.labelService.getContext(this.settings.context);
    }

    this.form = this.fb.group({
      referralCounts: [true],
      context: [this.prevContextValue, Validators.required],
      typeId: [this.settings.typeId, Validators.required],

      organisationId: [this.settings.organisationId || -1, Validators.required],

      label: [this.settings.label || 'Referral Count'],
      title: [this.settings.title || 'Referral Count'],
      showExtra: [this.settings.showExtra],
      extraTypeId: [this.settings.extraTypeId],

      extraLabel: [this.settings.extraLabel || ''],
    });

    // set colour
    this.color = this.settings.color;
    this.originalColor = this.settings.color;

    this.formControlValueChanged();
    this.patchTitleAndLabels(this.settings);

    Utilities.patchCheckboxAria();
  }

  ngOnDestroy() {
    this.onTypesChanged?.unsubscribe();
    this.activeUsers?.unsubscribe();
    this.onExtraControlValueChanges?.unsubscribe();
    this.onOrganisationsChanged?.unsubscribe();
  }

  formControlValueChanged() {

    const extraTypeIdControl = this.form.get('extraTypeId');
    const showExtraControl = this.form.get('showExtra');

    if (showExtraControl && extraTypeIdControl) {
      this.onExtraControlValueChanges = showExtraControl.valueChanges.subscribe((val) => {
        if (val) {
          extraTypeIdControl.setValidators([Validators.required]);
          this.displayExtra = true;
          setTimeout(() => { this.displayExtra = false; }, 100);
        }
        else {
          extraTypeIdControl.clearValidators();
        }
        extraTypeIdControl.updateValueAndValidity();
      });
    }
  }

  onColorChange(color) {
    if (color && (this.originalColor !== color)) {
      this.form.markAsDirty();
    }
  }

  contextChange(ev: MatSelectChange) {
    const val = ev.value;
    if (val && Array.isArray(val)) {
      const newValues = (this.prevContextValue && (this.prevContextValue.length > 0)) ? val.filter((i) => !this.prevContextValue.includes(i)) : val;
      const cValues = newValues.filter((i) => i < 1);
      const patchValue = (cValues.length > 0) ? [cValues[0]] : val.filter((i) => i > 0);
      this.form.controls.context.patchValue(patchValue);
      this.prevContextValue = patchValue;
    } else {
      this.prevContextValue = ev.value;
    }
    this.selectionChange(ev);
  }

  subjectTypeChange(ev: MatSelectChange) {
    const val = ev.value;
    this.currentSubjectTypeId = val;

    if (this.showOrganisations) {
      const subjectType = this.types.find((i) => i.id === val);
      if (subjectType) {
        this.hasOrganisation = this.organisationService.hasOrganisation(subjectType);
        this.organisationLabel = this.organisationService.organisationLabel(subjectType.name);
      }
    }

    this.selectionChange(ev);
  }

  subjectExtraTypeChange(ev: MatSelectChange) {
    this.currentExtraSubjectTypeId = ev.value;;
    this.selectionChange(ev);
  }

  markLabelAsChanged() {
    this.labelChanged = true;
  }

  markTitleAsChanged() {
    this.titleChanged = true;
  }

  markExtraLabelAsChanged() {
    this.extraLabelChanged = true;
  }

  selectionChange(ev: MatSelectChange) {
    const data: CountWidgetSettings = this.form.getRawValue();
    this.patchTitleAndLabels(data);
  }

  patchTitleAndLabels(settings: CountWidgetSettings) {
    if (!this.labelChanged) {
      this.form.controls.label.patchValue(this.labelService.getDefaultCountLabel(settings).toUpperCase());
    }

    if (!this.titleChanged) {
      this.form.controls.title.patchValue(this.labelService.getDefaultTitle(settings, undefined));
    }

    if (!this.extraLabelChanged && settings.showExtra) {
      this.form.controls.extraLabel.patchValue(this.labelService.getDefaultExtraCountLabel(settings));
    }
  }

  save() {
    const data = this.form.getRawValue();
    if (data.label === this.labelService.getDefaultCountLabel(data)) {
      data.label = undefined;
    }
    if (data.title === this.labelService.getDefaultTitle(data, undefined)) {
      data.title = undefined;
    }
    if (data.showExtra && (data.extraLabel === this.labelService.getDefaultExtraCountLabel(data))) {
      data.extraLabel = undefined;
    }
    if (!this.hasOrganisation) {
      data.organisationId = -1;
    }
    data.color = this.color;
    this.dialogRef.close({ data });
  }
}
